import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import AdminLayout from '../AdminLayout';
import { API } from '../../../lib/api';
import { toast } from 'sonner';
import {
  RefreshCw, Clock, CheckCircle, XCircle, Play, Pause, Trash2,
  MessageSquare, Bell, Mail, User, Calendar, AlertTriangle
} from 'lucide-react';

// Status badge
const StatusBadge = ({ status }) => {
  const styles = {
    queued: 'bg-blue-100 text-blue-700',
    processing: 'bg-yellow-100 text-yellow-700',
    delivered: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    cancelled: 'bg-gray-100 text-gray-500',
    skipped: 'bg-gray-100 text-gray-500',
    retrying: 'bg-orange-100 text-orange-700',
  };
  
  const labels = {
    queued: 'In Coda',
    processing: 'In Elaborazione',
    delivered: 'Consegnato',
    failed: 'Fallito',
    cancelled: 'Annullato',
    skipped: 'Saltato',
    retrying: 'Da Ritentare',
  };
  
  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.queued}`}>
      {labels[status] || status}
    </span>
  );
};

// Channel icon
const ChannelIcon = ({ channel }) => {
  const icons = {
    whatsapp: MessageSquare,
    push: Bell,
    email: Mail,
  };
  const Icon = icons[channel] || MessageSquare;
  return <Icon className="w-4 h-4" />;
};

// Queue Message Row
const QueueRow = ({ message, onAction }) => {
  return (
    <div className="flex items-center justify-between p-4 hover:bg-gray-50 border-b border-gray-100 transition-colors">
      <div className="flex items-center gap-4 min-w-0 flex-1">
        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
          <ChannelIcon channel={message.channel} />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {message.guest_name || message.guest_id}
          </p>
          <p className="text-xs text-gray-500">
            {message.template_name || message.template_id}
            {message.booking_code && ` • ${message.booking_code}`}
          </p>
        </div>
      </div>
      
      <div className="flex items-center gap-4">
        <div className="text-right text-sm hidden md:block">
          <p className="text-gray-900">
            {message.scheduled_at && new Date(message.scheduled_at).toLocaleString('it-IT', {
              day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
            })}
          </p>
          <p className="text-xs text-gray-500">
            Retry: {message.retry_count || 0}
          </p>
        </div>
        
        <StatusBadge status={message.status} />
        
        <div className="flex gap-1">
          {message.status === 'queued' && (
            <>
              <button
                onClick={() => onAction(message.id, 'execute_now')}
                className="p-2 hover:bg-green-100 rounded-lg transition-colors"
                title="Esegui ora"
              >
                <Play className="w-4 h-4 text-green-600" />
              </button>
              <button
                onClick={() => onAction(message.id, 'skip')}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
                title="Salta"
              >
                <Pause className="w-4 h-4 text-gray-600" />
              </button>
            </>
          )}
          {message.status === 'failed' && (
            <button
              onClick={() => onAction(message.id, 'execute_now')}
              className="p-2 hover:bg-orange-100 rounded-lg transition-colors"
              title="Ritenta"
            >
              <RefreshCw className="w-4 h-4 text-orange-600" />
            </button>
          )}
          <button
            onClick={() => onAction(message.id, 'cancel')}
            className="p-2 hover:bg-red-100 rounded-lg transition-colors"
            title="Cancella"
          >
            <Trash2 className="w-4 h-4 text-red-600" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default function QueuePage() {
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const fetchQueue = async () => {
    try {
      const url = filter 
        ? `${API}/admin/communication/queue?status=${filter}&limit=100`
        : `${API}/admin/communication/queue?limit=100`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
      }
    } catch (error) {
      console.error('Error fetching queue:', error);
      toast.error('Errore nel caricamento coda');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchQueue();
      const interval = setInterval(fetchQueue, 10000);
      return () => clearInterval(interval);
    }
  }, [token, filter]);

  const handleAction = async (messageId, action) => {
    try {
      const response = await fetch(`${API}/admin/communication/queue/${messageId}/action`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action })
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchQueue();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nell\'azione');
      }
    } catch (error) {
      toast.error('Errore di rete');
    }
  };

  const statusFilters = [
    { value: '', label: 'Tutti' },
    { value: 'queued', label: 'In Coda' },
    { value: 'processing', label: 'In Elaborazione' },
    { value: 'failed', label: 'Falliti' },
    { value: 'retrying', label: 'Da Ritentare' },
  ];

  return (
    <AdminLayout title="Communication Queue">
      <div className="p-4 md:p-6 space-y-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Coda Comunicazioni</h1>
            <p className="text-gray-500 mt-1">Monitora e gestisci i messaggi in coda</p>
          </div>
          <button
            onClick={fetchQueue}
            className="p-2.5 bg-white hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors shadow-sm"
            title="Aggiorna"
          >
            <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-2 flex-wrap">
          {statusFilters.map(f => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f.value 
                  ? 'bg-[#C5A059] text-white' 
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Queue List */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 mx-auto animate-spin text-gray-400" />
            </div>
          ) : messages.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Coda vuota</h3>
              <p className="text-gray-500">Nessun messaggio in attesa</p>
            </div>
          ) : (
            <div>
              {messages.map(message => (
                <QueueRow
                  key={message.id}
                  message={message}
                  onAction={handleAction}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
